import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Text, View, XStack, YStack } from "tamagui";

import { colors } from "@styles/colors";
import { appToast } from "@components/Toast";
import { TapPaySecurityNote } from "@components/payment/TapPayCompliance";
import { TAPPAY_SERVER_TYPE, loadTapPaySdk } from "@utils/tappaySdk";

const APP_ID = Number(process.env.REACT_APP_TAPPAY_APP_ID) || 0;
const APP_KEY = process.env.REACT_APP_TAPPAY_APP_KEY || "";

// TapPay Fields 同頁不能掛兩組同名元素：與綁卡頁、GasShop、Kiosk 的 id 都分開。
const IDS = {
  number: "guest-pay-card-number",
  exp: "guest-pay-card-expiration-date",
  ccv: "guest-pay-card-ccv",
} as const;

export type OneTimeCardState = { sdkReady: boolean; canPay: boolean; gettingPrime: boolean };

export type OneTimeCardFormHandle = {
  /** 取 prime 後交給 onPrime；卡號還沒填好或正在取時不動作 */
  pay: () => void;
};

/**
 * 訪客付款的信用卡單次刷卡欄位：TapPay Fields 取 prime 交給呼叫端扣款，不留卡、不綁卡。
 * 卡號只在 TapPay 的 iframe 裡，不經本站。付款按鈕由頁面底部的固定列負責（ref.pay()），
 * 欄位狀態用 onStateChange 回報，讓按鈕知道能不能按。
 */
const OneTimeCardForm = forwardRef<OneTimeCardFormHandle, { onPrime: (prime: string) => void; onStateChange: (s: OneTimeCardState) => void }>(
  ({ onPrime, onStateChange }, ref) => {
    const [sdkReady, setSdkReady] = useState(false);
    const [sdkError, setSdkError] = useState(false);
    const [canGetPrime, setCanGetPrime] = useState(false);
    const [gettingPrime, setGettingPrime] = useState(false);
    const inited = useRef(false);

    useEffect(() => {
      let alive = true;
      loadTapPaySdk()
        .then(() => {
          if (!alive || inited.current || !window.TPDirect) return;
          inited.current = true;
          window.TPDirect.setupSDK(APP_ID, APP_KEY, TAPPAY_SERVER_TYPE);
          window.__tappaySdkSetup = true;
          window.TPDirect.card.setup({
            fields: {
              number: { element: `#${IDS.number}`, placeholder: "**** **** **** ****" },
              expirationDate: { element: `#${IDS.exp}`, placeholder: "MM / YY" },
              ccv: { element: `#${IDS.ccv}`, placeholder: "CCV" },
            },
            styles: {
              input: { color: colors.text, "font-size": "16px", "font-weight": "700" },
              "input.valid": { color: colors.green },
              "input.invalid": { color: colors.danger },
            },
          });
          window.TPDirect.card.onUpdate((u: { canGetPrime?: boolean }) => setCanGetPrime(!!u?.canGetPrime));
          setSdkReady(true);
        })
        .catch(() => {
          if (alive) setSdkError(true);
        });
      return () => {
        alive = false;
      };
    }, []);

    useEffect(() => {
      onStateChange({ sdkReady, canPay: sdkReady && canGetPrime && !gettingPrime, gettingPrime });
    }, [sdkReady, canGetPrime, gettingPrime, onStateChange]);

    useImperativeHandle(
      ref,
      () => ({
        pay: () => {
          if (!sdkReady || !canGetPrime || gettingPrime || !window.TPDirect) return;
          setGettingPrime(true);
          window.TPDirect.card.getPrime((result: { status: number; msg?: string; card?: { prime?: string } }) => {
            setGettingPrime(false);
            const prime = result?.card?.prime;
            if (result.status !== 0 || !prime) {
              appToast.error(`卡片資料有誤：${result.msg || "請確認卡號、有效期限與安全碼"}`);
              return;
            }
            onPrime(prime);
          });
        },
      }),
      [sdkReady, canGetPrime, gettingPrime, onPrime],
    );

    const field = (label: string, id: string, flex?: number, width?: number) => (
      <YStack flex={flex} width={width} gap={4} bg={colors.bg} br={10} px="$3" py="$2" bw={1} bc={colors.border}>
        <Text fontSize={11} fontWeight="700" color={colors.hint}>
          {label}
        </Text>
        <View id={id} h={26} />
      </YStack>
    );

    return (
      <YStack gap="$2.5">
        {sdkError ? (
          <Text fontSize={13} color={colors.danger}>
            卡號欄位載入失敗，請重新整理頁面
          </Text>
        ) : null}
        {field("卡號", IDS.number)}
        <XStack gap="$2.5">
          {field("有效期限", IDS.exp, 1)}
          {field("安全碼", IDS.ccv, undefined, 112)}
        </XStack>
        {/* TapPay 審核要求刷卡畫面揭露全文（審核項 6），不可縮短，只用精簡排版 */}
        <TapPaySecurityNote compact />
      </YStack>
    );
  },
);

OneTimeCardForm.displayName = "OneTimeCardForm";

export default OneTimeCardForm;
